import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, Copy, Check, ArrowUpRight, Info, Printer } from "lucide-react";
import Header from "../components/landing/Header";
import { CarouselBox } from "../components/brand/CarouselBox";
import { IRL_SLIDES } from "../lib/brandCarousels";
import Footer from "../components/landing/Footer";
import Seo from "../components/Seo";
import { ToolEmailCapture } from "../components/ToolEmailCapture";
import { IrlOptions } from "../components/IrlOptions";
import api from "../lib/api";

const euro = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const num = (v) => { const n = parseFloat(String(v || "").replace(/\s/g, "").replace(",", ".")); return isNaN(n) ? 0 : n; };
const QLABEL = { T1: "1er trimestre", T2: "2e trimestre", T3: "3e trimestre", T4: "4e trimestre" };
const quarterLabel = (q) => { const [y, t] = (q || "").split("-"); return `${QLABEL[t] || t} ${y}`; };
const inp = "w-full px-3 py-2.5 rounded-xl border border-brand-soft bg-white outline-none focus:border-brand-lime text-sm text-brand-greenDeep";
const lbl = "block text-xs font-semibold text-brand-green/70 mb-1.5";

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "WebApplication",
  name: "Calculateur de révision de loyer (IRL)",
  url: "https://www.360immoinvest.com/revision-loyer-irl/calculateur",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  inLanguage: "fr-FR",
  offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
  publisher: { "@type": "Organization", name: "360immoinvest", url: "https://www.360immoinvest.com/" },
};

const STORAGE_KEY = "irl_calc_state";
const loadSaved = () => { try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; } };

export default function CalculateurIrl() {
  const saved = loadSaved();
  const [loyer, setLoyer] = useState(saved.loyer || "");
  const [irlNew, setIrlNew] = useState(saved.irlNew || "");
  const [irlNewQ, setIrlNewQ] = useState(saved.irlNewQ || "");
  const [irlOld, setIrlOld] = useState(saved.irlOld || "");
  const [irlOldQ, setIrlOldQ] = useState(saved.irlOldQ || "");
  const [copied, setCopied] = useState(false);
  const [table, setTable] = useState([]);
  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/irl/public-index").then((r) => setTable(r.data.table || [])).catch(() => {});
  }, []);
  // Mémorise les saisies tant que l'utilisateur reste sur la page
  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ loyer, irlNew, irlNewQ, irlOld, irlOldQ }));
  }, [loyer, irlNew, irlNewQ, irlOld, irlOldQ]);
  useEffect(() => () => sessionStorage.removeItem(STORAGE_KEY), []);

  const pickNew = (q) => {
    setIrlNewQ(q);
    if (!q) return;
    const cur = table.find((t) => t.quarter === q);
    if (cur) setIrlNew(String(cur.value).replace(".", ","));
    // Pré-remplit l'année précédente si non encore choisie
    if (!irlOldQ) {
      const [y, t] = q.split("-");
      const prev = table.find((x) => x.quarter === `${Number(y) - 1}-${t}`);
      if (prev) { setIrlOldQ(prev.quarter); setIrlOld(String(prev.value).replace(".", ",")); }
    }
  };
  const pickOld = (q) => {
    setIrlOldQ(q);
    if (!q) return;
    const cur = table.find((t) => t.quarter === q);
    if (cur) setIrlOld(String(cur.value).replace(".", ","));
  };

  const l = num(loyer);
  const inew = num(irlNew);
  const iold = num(irlOld);
  const valid = l > 0 && inew > 0 && iold > 0;
  const newLoyer = valid ? l * (inew / iold) : 0;
  const augmentMois = valid ? newLoyer - l : 0;
  const augmentAn = augmentMois * 12;
  const pct = valid ? ((inew / iold) - 1) * 100 : 0;
  const baisse = valid && inew < iold;

  const copyText = async () => {
    if (!valid) return;
    const txt = [
      "Révision de loyer (IRL)",
      `Loyer actuel : ${euro.format(l)}`,
      `IRL de référence (année en cours) : ${inew}`,
      `IRL de référence (année précédente) : ${iold}`,
      `Nouveau loyer : ${euro.format(newLoyer)}`,
      `Variation : ${augmentMois >= 0 ? "+" : ""}${euro.format(augmentMois)} / mois (${augmentAn >= 0 ? "+" : ""}${euro.format(augmentAn)} / an)`,
      `Évolution de l'indice : ${pct >= 0 ? "+" : ""}${pct.toFixed(2)} %`,
    ].join("\n");
    try { await navigator.clipboard.writeText(txt); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* clipboard indisponible */ }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="calculateur-irl-page">
      <style>{`@media screen { #irl-sheet { display: none; } }
      @media print {
        body * { visibility: hidden !important; }
        #irl-sheet, #irl-sheet * { visibility: visible !important; }
        #irl-sheet { display: block !important; position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
      }`}</style>
      <Seo
        title="Calculateur de révision de loyer (IRL) gratuit | 360immoinvest"
        description="Calculez le nouveau loyer après révision annuelle avec l'indice de référence des loyers (IRL). Gratuit, sans inscription, résultat immédiat."
        path="/revision-loyer-irl/calculateur"
        jsonLd={JSON_LD}
      />
      <Header />
      <main>
        <section className="pt-32 pb-8 bg-brand-ivoire">
          <div className="max-w-5xl mx-auto px-5 lg:px-8">
            <p className="text-brand-limeDark font-semibold mb-2">Outil gratuit</p>
            <h1 className="font-display text-4xl lg:text-5xl font-semibold text-brand-greenDeep leading-tight">Calculateur de révision de loyer (IRL)</h1>
            <p className="mt-4 text-lg text-brand-green/70 max-w-2xl">Estimez le nouveau loyer après la révision annuelle. Gratuit, sans inscription, résultat immédiat.</p>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-5 lg:px-8 pb-16 grid lg:grid-cols-2 gap-8 items-start">
          {/* Formulaire */}
          <div className="rounded-2xl border border-brand-soft p-6 space-y-4">
            <div>
              <label className={lbl}>Loyer actuel hors charges (€)</label>
              <input inputMode="decimal" placeholder="650,00" className={inp} value={loyer} onChange={(e) => setLoyer(e.target.value)} data-testid="irl-loyer" />
            </div>
            <div>
              <label className={lbl}>IRL de référence — trimestre de l'année en cours</label>
              {table.length > 0 && (
                <select className={`${inp} mb-2`} value={irlNewQ} onChange={(e) => pickNew(e.target.value)} data-testid="irl-new-select">
                  <option value="">— Choisir un trimestre ({table.length} depuis {table[table.length - 1].quarter.slice(0, 4)}) —</option>
                  <IrlOptions table={table} label={quarterLabel} />
                </select>
              )}
              <input inputMode="decimal" placeholder="145,17" className={inp} value={irlNew} onChange={(e) => { setIrlNew(e.target.value); setIrlNewQ(""); }} data-testid="irl-new" />
            </div>
            <div>
              <label className={lbl}>IRL de l'année précédente ou de la dernière application de l'IRL</label>
              {table.length > 0 && (
                <select className={`${inp} mb-2`} value={irlOldQ} onChange={(e) => pickOld(e.target.value)} data-testid="irl-old-select">
                  <option value="">— Choisir un trimestre ({table.length} depuis {table[table.length - 1].quarter.slice(0, 4)}) —</option>
                  <IrlOptions table={table} label={quarterLabel} />
                </select>
              )}
              <input inputMode="decimal" placeholder="140,59" className={inp} value={irlOld} onChange={(e) => { setIrlOld(e.target.value); setIrlOldQ(""); }} data-testid="irl-old" />
            </div>
            <div className="flex items-start gap-2 text-xs text-brand-green/55 bg-brand-softer rounded-xl p-3">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Les valeurs de l'IRL sont publiées chaque trimestre par l'Insee. Utilisez le trimestre de référence indiqué dans votre bail (à défaut, le dernier indice connu à la signature).</span>
            </div>
          </div>

          {/* Résultat */}
          <div className="rounded-2xl bg-brand-greenDeep text-white p-8" data-testid="irl-result">
            <div className="flex items-center gap-2 text-brand-lime font-semibold"><Calculator className="w-5 h-5" /> Résultat</div>
            {valid ? (
              <>
                <p className="text-white/70 text-sm mt-4">Nouveau loyer hors charges</p>
                <p className="font-display text-5xl font-bold mt-1" data-testid="irl-newloyer">{euro.format(newLoyer)}</p>
                <div className="mt-6 space-y-2 text-sm">
                  <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/70">Loyer actuel</span><span>{euro.format(l)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/70">{baisse ? "Baisse" : "Hausse"} mensuelle</span><span>{augmentMois >= 0 ? "+" : ""}{euro.format(augmentMois)}</span></div>
                  <div className="flex justify-between border-b border-white/10 pb-2"><span className="text-white/70">Sur l'année</span><span>{augmentAn >= 0 ? "+" : ""}{euro.format(augmentAn)}</span></div>
                  <div className="flex justify-between"><span className="text-white/70">Évolution de l'indice</span><span>{pct >= 0 ? "+" : ""}{pct.toFixed(2)} %</span></div>
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button onClick={copyText} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-semibold" data-testid="irl-copy">
                    {copied ? <Check className="w-4 h-4 text-brand-lime" /> : <Copy className="w-4 h-4" />} {copied ? "Copié" : "Copier le résultat"}
                  </button>
                  <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep text-sm font-semibold" data-testid="irl-print">
                    <Printer className="w-4 h-4" /> Imprimer / PDF
                  </button>
                </div>
                <ToolEmailCapture id="irl-mail" dark title="Recevoir ce résultat par e-mail" text="Gardez une trace de votre calcul, et recevez un rappel avant la prochaine révision annuelle." button="Recevoir" reminderLabel="Me rappeler dans 11 mois de réviser mon loyer" onSend={({ email, consent, reminder }) => api.post("/outils/irl/email", { email, consent, reminder, result: { loyer: euro.format(l), new_loyer: euro.format(newLoyer), irl_new: irlNew, irl_new_q: irlNewQ, irl_old: irlOld, irl_old_q: irlOldQ, pct: `${pct >= 0 ? "+" : ""}${pct.toFixed(2)} %` } })} />
              </>
            ) : (
              <p className="text-white/60 mt-4 text-sm">Renseignez le loyer et les deux valeurs d'IRL pour obtenir le nouveau loyer.</p>
            )}
          </div>
        </div>
        {valid && (
          <div className="max-w-5xl mx-auto px-5 lg:px-8 pb-10">
            <CarouselBox slides={IRL_SLIDES} name="revision-irl" testid="irl-carousel"
              data={{ loyer: l, iold, inew, oldQ: quarterLabel(irlOldQ), newQ: quarterLabel(irlNewQ), newLoyer, augmentMois, augmentAn, pct, baisse }} />
          </div>
        )}

        <section className="bg-brand-softer py-14">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            <div className="rounded-3xl bg-white border border-brand-soft p-7">
              <h2 className="font-display text-xl font-semibold text-brand-greenDeep">Comment est calculé le nouveau loyer ?</h2>
              <p className="mt-2 text-brand-green/75 text-[15px] leading-relaxed">Nouveau loyer = Loyer actuel × (IRL du trimestre de référence de l'année en cours ÷ IRL du même trimestre de l'année précédente). La révision n'est possible que si le bail prévoit une clause de révision, une fois par an, et dans la limite d'un an après la date prévue.</p>
              <p className="mt-3 text-xs text-brand-green/45">Ce calcul est fourni à titre indicatif et ne remplace pas un conseil juridique.</p>
            </div>
            <div className="mt-8 rounded-3xl bg-brand-greenDeep text-white p-8 text-center">
              <h2 className="font-display text-2xl font-semibold">Automatisez vos révisions de loyer</h2>
              <p className="mt-2 text-white/75 text-[15px]">360immoinvest récupère l'indice IRL à jour et applique la révision automatiquement. Gratuit pour 1 bien.</p>
              <Link to="/connexion" className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-lime hover:bg-brand-limeDark text-brand-greenDeep font-semibold" data-testid="irl-cta">Créer mon compte gratuit <ArrowUpRight className="w-5 h-5" /></Link>
            </div>
            <p className="mt-6 text-center text-sm text-brand-green/60">Pour en savoir plus, lisez notre <Link to="/revision-loyer-irl" className="text-brand-greenDeep underline font-medium">guide sur la révision de loyer</Link>.</p>
          </div>
        </section>
      </main>

      {/* Feuille imprimable */}
      <div id="irl-sheet" className="text-[14px] text-brand-green/90 leading-relaxed">
        <h2 style={{ fontSize: "22px", fontWeight: 700, color: "#0f5c56" }}>Calcul de révision de loyer (IRL)</h2>
        <p style={{ color: "#56605b", marginTop: 4 }}>Document indicatif généré sur 360immoinvest — {new Date().toLocaleDateString("fr-FR")}</p>
        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20 }}>
          <tbody>
            <tr><td style={{ padding: "8px 0", color: "#56605b" }}>Loyer actuel hors charges</td><td style={{ padding: "8px 0", textAlign: "right" }}>{euro.format(l)}</td></tr>
            <tr><td style={{ padding: "8px 0", color: "#56605b" }}>IRL année en cours{irlNewQ ? ` (${quarterLabel(irlNewQ)})` : ""}</td><td style={{ padding: "8px 0", textAlign: "right" }}>{inew || "—"}</td></tr>
            <tr><td style={{ padding: "8px 0", color: "#56605b" }}>IRL précédent / dernière application{irlOldQ ? ` (${quarterLabel(irlOldQ)})` : ""}</td><td style={{ padding: "8px 0", textAlign: "right" }}>{iold || "—"}</td></tr>
            <tr><td style={{ padding: "8px 0", color: "#56605b" }}>Évolution de l'indice</td><td style={{ padding: "8px 0", textAlign: "right" }}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)} %</td></tr>
            <tr><td style={{ padding: "10px 0", fontWeight: 700, color: "#0f5c56", borderTop: "1px solid #d5dad6" }}>Nouveau loyer hors charges</td><td style={{ padding: "10px 0", textAlign: "right", fontWeight: 700, color: "#0f5c56", borderTop: "1px solid #d5dad6" }}>{euro.format(newLoyer)}</td></tr>
            <tr><td style={{ padding: "8px 0", color: "#56605b" }}>{baisse ? "Baisse" : "Hausse"} mensuelle / annuelle</td><td style={{ padding: "8px 0", textAlign: "right" }}>{augmentMois >= 0 ? "+" : ""}{euro.format(augmentMois)} / {augmentAn >= 0 ? "+" : ""}{euro.format(augmentAn)}</td></tr>
          </tbody>
        </table>
        <p style={{ marginTop: 16, fontSize: "12px", color: "#8a8a80" }}>Formule : nouveau loyer = loyer actuel × (IRL année en cours ÷ IRL année précédente). Calcul fourni à titre indicatif, ne remplace pas un conseil juridique.</p>
      </div>
      <Footer />
    </div>
  );
}
